"use strict";
// Copyright © Veeam Software Group GmbH
Object.defineProperty(exports, "__esModule", { value: true });
exports.MonthlyScheduleHarmonizationManager = void 0;
const baseTypes_1 = require("../../core/baseTypes");
const helpers_1 = require("../../helpers");
const constants_1 = require("./constants");
const helpers_2 = require("./helpers");
require("./models");
const ScheduleHarmonizationManager_1 = require("./ScheduleHarmonizationManager");
const types_1 = require("./types");
const WeeklyScheduleHarmonizationManager_1 = require("./WeeklyScheduleHarmonizationManager");
class MonthlyScheduleHarmonizationManager extends ScheduleHarmonizationManager_1.ScheduleHarmonizationManager {
    constructor(mainModel, controlModel, models, retentionFieldsSettings) {
        super(Object.assign({ monthlyScheduleModel: mainModel }, models));
        this.mainModel = mainModel;
        this.controlModel = controlModel;
        this.models = models;
        this.retentionFieldsSettings = retentionFieldsSettings;
        this.isArchiveNotificationVisible = (retentionMaxMonths, retentionMaxDays) => {
            if (!this.controlModel) {
                return false;
            }
            const controlModelValue = this.controlModel.getValue();
            return (controlModelValue.isArchiveEnabled === false &&
                ((controlModelValue.rowThirdFieldTypeValue === baseTypes_1.RETENTION_TYPE.months &&
                    controlModelValue.rowThirdFieldCountValue >= retentionMaxMonths) ||
                    (controlModelValue.rowThirdFieldTypeValue === baseTypes_1.RETENTION_TYPE.days &&
                        controlModelValue.rowThirdFieldCountValue >= retentionMaxDays)));
        };
        this.getFieldDayNumberInMonthOptions = () => {
            const dailyScheduleModelValue = this.models.dailyScheduleModel.getValue();
            const weeklyScheduleModelValue = this.models.weeklyScheduleModel.getValue();
            return MonthlyScheduleHarmonizationManager.getFieldDayNumberInMonthOptionsData(dailyScheduleModelValue, weeklyScheduleModelValue);
        };
        this.getFieldTimeOptions = (isArchive) => {
            const dailyScheduleModelValue = this.models.dailyScheduleModel.getValue();
            const weeklyScheduleModelValue = this.models.weeklyScheduleModel.getValue();
            return MonthlyScheduleHarmonizationManager.getFieldTimeOptionsData(isArchive, dailyScheduleModelValue, weeklyScheduleModelValue);
        };
        this.isFieldTimeDisabled = () => {
            var _a;
            const modelValue = (_a = this.controlModel) === null || _a === void 0 ? void 0 : _a.getValue();
            const isArchive = (modelValue === null || modelValue === void 0 ? void 0 : modelValue.isArchiveEnabled) || false;
            const fieldTimeOptions = this.getFieldTimeOptions(isArchive);
            return fieldTimeOptions.length === 1;
        };
        this.getFieldDayOfWeekOptions = (isArchive) => {
            const dailyScheduleModelValue = this.models.dailyScheduleModel.getValue();
            const weeklyScheduleModelValue = this.models.weeklyScheduleModel.getValue();
            return MonthlyScheduleHarmonizationManager.getFieldDayOfWeekOptionsData(isArchive, dailyScheduleModelValue, weeklyScheduleModelValue);
        };
        this.isFieldDayOfWeekDisabled = () => {
            var _a;
            const modelValue = (_a = this.controlModel) === null || _a === void 0 ? void 0 : _a.getValue();
            const isArchive = (modelValue === null || modelValue === void 0 ? void 0 : modelValue.isArchiveEnabled) || false;
            const fieldTimeOptions = this.getFieldDayOfWeekOptions(isArchive);
            return fieldTimeOptions.length === 1;
        };
        this.getAvailableOptions = () => {
            const dailyScheduleModelValue = this.models.dailyScheduleModel.getValue();
            const weeklyScheduleModelValue = this.models.weeklyScheduleModel.getValue();
            return MonthlyScheduleHarmonizationManager.getAvailableOptionsData(dailyScheduleModelValue, weeklyScheduleModelValue);
        };
        this.shouldArchiveBeEnabled = (currentValue) => {
            if (currentValue === false) {
                return false;
            }
            return this.canArchiveBeEnabled();
        };
        this.harmonize = (shouldResetSchedule = true) => {
            const modelValue = this.mainModel.getValue();
            const dailyScheduleModelValue = this.models.dailyScheduleModel.getValue();
            const weeklyScheduleModelValue = this.models.weeklyScheduleModel.getValue();
            const isArchiveEnabled = this.shouldArchiveBeEnabled(modelValue.isArchiveEnabled);
            const isReset = shouldResetSchedule && !modelValue.isEnabled && modelValue.isModified;
            // reset
            if (isReset) {
                const initialValue = MonthlyScheduleHarmonizationManager.getMainModelInitialValue(modelValue.rows.first.isEnabled, modelValue.rows.second.isEnabled, modelValue.rows.third.isEnabled, modelValue.rows.first.fields.isTypeVisible, modelValue.rows.second.fields.isTypeVisible, modelValue.rows.third.fields.isTypeVisible, this.retentionFieldsSettings, dailyScheduleModelValue, weeklyScheduleModelValue);
                return Object.assign(Object.assign({}, initialValue), { isArchiveVisible: modelValue.isArchiveVisible });
            }
            let restorePoint = MonthlyScheduleHarmonizationManager.getCurrentOrFirstInListItem(modelValue.fields.time, this.getFieldTimeOptions(isArchiveEnabled));
            let dayOfWeek = MonthlyScheduleHarmonizationManager.getCurrentOrFirstInListItem(modelValue.fields.dayOfWeek, this.getFieldDayOfWeekOptions(isArchiveEnabled));
            const dayNumberInMonth = MonthlyScheduleHarmonizationManager.getCurrentOrFirstInListItem(modelValue.fields.dayNumberInMonth, this.getFieldDayNumberInMonthOptions());
            return Object.assign(Object.assign({}, modelValue), { isArchiveEnabled, fields: Object.assign(Object.assign({}, modelValue.fields), { time: restorePoint, dayOfWeek,
                    dayNumberInMonth }) });
        };
        this.harmonizeControl = () => {
            var _a;
            const modelValue = (_a = this.controlModel) === null || _a === void 0 ? void 0 : _a.getValue();
            const isArchiveEnabled = (modelValue === null || modelValue === void 0 ? void 0 : modelValue.isArchiveEnabled) || false;
            if (!modelValue) {
                throw Error('Control Model does not exists');
            }
            let restorePoint = MonthlyScheduleHarmonizationManager.getCurrentOrFirstInListItem(modelValue.fieldTimeValue, this.getFieldTimeOptions(isArchiveEnabled));
            let dayOfWeek = MonthlyScheduleHarmonizationManager.getCurrentOrFirstInListItem(modelValue.fieldDayOfWeekValue, this.getFieldDayOfWeekOptions(isArchiveEnabled));
            const dayNumberInMonth = MonthlyScheduleHarmonizationManager.getCurrentOrFirstInListItem(modelValue.fieldDayNumberInMonthValue, this.getFieldDayNumberInMonthOptions());
            return Object.assign(Object.assign({}, modelValue), { fieldTimeValue: restorePoint, fieldDayOfWeekValue: dayOfWeek, fieldDayNumberInMonthValue: dayNumberInMonth });
        };
        this.mainModel = mainModel;
        this.controlModel = controlModel;
        this.models = models;
        this.retentionFieldsSettings = retentionFieldsSettings;
    }
}
exports.MonthlyScheduleHarmonizationManager = MonthlyScheduleHarmonizationManager;
MonthlyScheduleHarmonizationManager.ROW_THIRD_FIELD_COUNT_INITIAL_VALUE = constants_1.DEFAULT_MONTHLY_THIRD_ROW_RETENTION_COUNT;
MonthlyScheduleHarmonizationManager.ROW_FIRST_FIELD_COUNT_MIN_VALUE = constants_1.RETENTION_COUNT_MIN;
MonthlyScheduleHarmonizationManager.getMainModelInitialValue = (isFirstRowScheduleVisible = true, isSecondRowScheduleVisible = true, isThirdRowScheduleVisible = true, isRowFirstFieldTypeVisible = false, isRowSecondFieldTypeVisible = false, isRowThirdFieldTypeVisible = true, retentionFieldsSettings, dailyScheduleModelValue, weeklyScheduleModelValue) => {
    const timeOptions = MonthlyScheduleHarmonizationManager.getFieldTimeOptionsData(false, dailyScheduleModelValue, weeklyScheduleModelValue);
    const dayOfWeekOptions = MonthlyScheduleHarmonizationManager.getFieldDayOfWeekOptionsData(false, dailyScheduleModelValue, weeklyScheduleModelValue);
    const dayNumberInMonthOptions = MonthlyScheduleHarmonizationManager.getFieldDayNumberInMonthOptionsData(dailyScheduleModelValue, weeklyScheduleModelValue);
    return {
        isEnabled: false,
        isModified: false,
        rows: {
            [types_1.SCHEDULE_ROW.first]: MonthlyScheduleHarmonizationManager.getRowFirstInitialValue(isFirstRowScheduleVisible, constants_1.MONTHS_PER_YEAR, retentionFieldsSettings.rowFirstFieldCountInitialValue, retentionFieldsSettings.rowFirstFieldTypeInitialValue, isRowFirstFieldTypeVisible),
            [types_1.SCHEDULE_ROW.second]: MonthlyScheduleHarmonizationManager.getRowSecondInitialValue(isSecondRowScheduleVisible, constants_1.MONTHS_PER_YEAR, retentionFieldsSettings.rowSecondFieldCountInitialValue, retentionFieldsSettings.rowSecondFieldTypeInitialValue, isRowSecondFieldTypeVisible),
            [types_1.SCHEDULE_ROW.third]: ScheduleHarmonizationManager_1.ScheduleHarmonizationManager.getRowInitialValue(isThirdRowScheduleVisible, constants_1.MONTHS_PER_YEAR, retentionFieldsSettings.rowThirdFieldCountInitialValue, retentionFieldsSettings.rowThirdFieldTypeInitialValue, isRowThirdFieldTypeVisible),
        },
        fields: Object.assign(Object.assign({}, MonthlyScheduleHarmonizationManager.getWeeklyMonthlyFieldsInitialValue()), { dayOfWeek: dayOfWeekOptions[0] || ScheduleHarmonizationManager_1.ScheduleHarmonizationManager.FIELD_DAY_OF_WEEK, time: timeOptions[0] || ScheduleHarmonizationManager_1.ScheduleHarmonizationManager.FIELD_TIME_INITIAL_VALUE, dayNumberInMonth: dayNumberInMonthOptions[0] || ScheduleHarmonizationManager_1.ScheduleHarmonizationManager.FIELD_DAY_NUMBER_IN_MONTH }),
        isArchiveEnabled: false,
        isArchiveVisible: false,
    };
};
MonthlyScheduleHarmonizationManager.getFieldDayNumberInMonthOptionsData = (dailyScheduleModelValue, weeklyScheduleModelValue) => {
    const availableOptions = MonthlyScheduleHarmonizationManager.getAvailableOptionsData(dailyScheduleModelValue, weeklyScheduleModelValue);
    // Maksim.Kovalevskii: it doesn't matter which row because in monthly it should be the same list everytime
    return availableOptions.dayNumberInMonth.firstRow;
};
MonthlyScheduleHarmonizationManager.getFieldTimeOptionsData = (isArchive, dailyScheduleModelValue, weeklyScheduleModelValue) => {
    const availableOptions = MonthlyScheduleHarmonizationManager.getAvailableOptionsData(dailyScheduleModelValue, weeklyScheduleModelValue);
    return MonthlyScheduleHarmonizationManager.getFirstOrThirdRowSchedule(!isArchive, availableOptions.time);
};
MonthlyScheduleHarmonizationManager.getFieldDayOfWeekOptionsData = (isArchive, dailyScheduleModelValue, weeklyScheduleModelValue) => {
    const availableOptions = MonthlyScheduleHarmonizationManager.getAvailableOptionsData(dailyScheduleModelValue, weeklyScheduleModelValue);
    return MonthlyScheduleHarmonizationManager.getFirstOrThirdRowSchedule(!isArchive, availableOptions.dayOfWeek);
};
MonthlyScheduleHarmonizationManager.getMonthCurrentOptionsData = (modelValue, row) => {
    const defaultMonths = helpers_2.getMonthsOfYearList();
    return modelValue.isEnabled
        ? helpers_2.getMonthDataFromSchedule(modelValue.rows[row].schedule)
        : defaultMonths;
};
MonthlyScheduleHarmonizationManager.getTimeCurrentOptionsData = (modelValue) => {
    const defaultTimes = helpers_1.getTimes();
    return modelValue.isEnabled ? [modelValue.fields.time] : defaultTimes;
};
MonthlyScheduleHarmonizationManager.getDayOfWeekCurrentOptionsData = (modelValue) => {
    const defaultDays = helpers_2.getEverydayList();
    return modelValue.isEnabled ? [modelValue.fields.dayOfWeek] : defaultDays;
};
MonthlyScheduleHarmonizationManager.getDayNumberInMonthCurrentOptionsData = (modelValue) => {
    const defaultDayNumberInMonth = helpers_2.getDayNumberInMonthList();
    return modelValue.isEnabled
        ? [modelValue.fields.dayNumberInMonth, types_1.DAY_NUMBER_IN_MONTH.onDay]
        : defaultDayNumberInMonth;
};
MonthlyScheduleHarmonizationManager.getCurrentStateData = (monthlyScheduleModelValue) => {
    const isOnDay = monthlyScheduleModelValue.fields.dayNumberInMonth === types_1.DAY_NUMBER_IN_MONTH.onDay;
    const allDays = helpers_2.getDaysOfWeekList();
    const allDayNumberInMonth = helpers_2.getDayNumberInMonthList();
    const monthOptionsFirstRow = MonthlyScheduleHarmonizationManager.getMonthCurrentOptionsData(monthlyScheduleModelValue, 'first');
    const monthOptionsSecondRow = MonthlyScheduleHarmonizationManager.getMonthCurrentOptionsData(monthlyScheduleModelValue, 'second');
    const monthOptionsThirdRow = MonthlyScheduleHarmonizationManager.getMonthCurrentOptionsData(monthlyScheduleModelValue, 'third');
    const timeOptions = MonthlyScheduleHarmonizationManager.getTimeCurrentOptionsData(monthlyScheduleModelValue);
    const dayOfWeekOptions = isOnDay
        ? allDays
        : MonthlyScheduleHarmonizationManager.getDayOfWeekCurrentOptionsData(monthlyScheduleModelValue);
    const dayNumberInMonth = isOnDay
        ? allDayNumberInMonth
        : MonthlyScheduleHarmonizationManager.getDayNumberInMonthCurrentOptionsData(monthlyScheduleModelValue);
    return {
        time: {
            firstRow: timeOptions,
            secondRow: timeOptions,
            thirdRow: timeOptions,
        },
        dayOfWeek: {
            firstRow: dayOfWeekOptions,
            secondRow: dayOfWeekOptions,
            thirdRow: dayOfWeekOptions,
        },
        monthOfYear: {
            firstRow: monthOptionsFirstRow,
            secondRow: monthOptionsSecondRow,
            thirdRow: monthOptionsThirdRow,
        },
        dayNumberInMonth: {
            firstRow: dayNumberInMonth,
            secondRow: dayNumberInMonth,
            thirdRow: dayNumberInMonth,
        },
    };
};
MonthlyScheduleHarmonizationManager.getAvailableOptionsData = (dailyScheduleModelValue, weeklyScheduleModelValue) => {
    const weeklyAvailableOptions = WeeklyScheduleHarmonizationManager_1.WeeklyScheduleHarmonizationManager.getAvailableOptionsData(dailyScheduleModelValue);
    const weeklyCurrentState = WeeklyScheduleHarmonizationManager_1.WeeklyScheduleHarmonizationManager.getCurrentStateData(weeklyScheduleModelValue);
    return {
        time: {
            firstRow: MonthlyScheduleHarmonizationManager.getAvailableOptionsRow('firstRow', weeklyCurrentState.time, weeklyAvailableOptions.time),
            secondRow: MonthlyScheduleHarmonizationManager.getAvailableOptionsRow('secondRow', weeklyCurrentState.time, weeklyAvailableOptions.time),
            thirdRow: MonthlyScheduleHarmonizationManager.getAvailableOptionsTimeThirdRow(weeklyScheduleModelValue.isEnabled, weeklyCurrentState.time, weeklyAvailableOptions.time),
        },
        dayOfWeek: {
            firstRow: MonthlyScheduleHarmonizationManager.getAvailableOptionsRow('firstRow', weeklyCurrentState.dayOfWeek, weeklyAvailableOptions.dayOfWeek),
            secondRow: MonthlyScheduleHarmonizationManager.getAvailableOptionsRow('secondRow', weeklyCurrentState.dayOfWeek, weeklyAvailableOptions.dayOfWeek),
            thirdRow: MonthlyScheduleHarmonizationManager.getAvailableOptionsRow('thirdRow', weeklyCurrentState.dayOfWeek, weeklyAvailableOptions.dayOfWeek),
        },
        monthOfYear: {
            firstRow: MonthlyScheduleHarmonizationManager.getAvailableOptionsRow('firstRow', weeklyCurrentState.monthOfYear, weeklyAvailableOptions.monthOfYear),
            secondRow: MonthlyScheduleHarmonizationManager.getAvailableOptionsRow('secondRow', weeklyCurrentState.monthOfYear, weeklyAvailableOptions.monthOfYear),
            thirdRow: MonthlyScheduleHarmonizationManager.getAvailableOptionsRow('thirdRow', weeklyCurrentState.monthOfYear, weeklyAvailableOptions.monthOfYear),
        },
        dayNumberInMonth: {
            firstRow: MonthlyScheduleHarmonizationManager.getAvailableOptionsRow('firstRow', weeklyCurrentState.dayNumberInMonth, weeklyAvailableOptions.dayNumberInMonth),
            secondRow: MonthlyScheduleHarmonizationManager.getAvailableOptionsRow('secondRow', weeklyCurrentState.dayNumberInMonth, weeklyAvailableOptions.dayNumberInMonth),
            thirdRow: MonthlyScheduleHarmonizationManager.getAvailableOptionsRow('thirdRow', weeklyCurrentState.dayNumberInMonth, weeklyAvailableOptions.dayNumberInMonth),
        },
    };
};
//# sourceMappingURL=MonthlyScheduleHarmonizationManager.js.map